if (!customElements.get('wallpaper-wizard')) {
  customElements.define(
    'wallpaper-wizard',
    class WallpaperWizard extends HTMLElement {
      constructor() {
        super();

        // State
        this.state = {
          unit: 'inches',
          rawWidth: null,
          rawHeight: null,
          adjustedWidthInches: null,
          adjustedHeightFeet: null,
          panelCount: null,
          areaSqFt: null,
          selectedMaterial: null,
          selectedVariantId: null,
          showMaterials: false,
          selectedSampleMaterial: null,
        };

        // Material metadata (subtitles + descriptions)
        this.materialMeta = {
          'LUXE Traditional': {
            subtitle: 'Premium non-woven wallpaper',
            description:
              'Our LUXE Traditional wallpaper is a premium non-woven material that is paste-the-wall for easy installation. It features a smooth, matte finish with rich color reproduction. Durable and washable, it is perfect for high-traffic areas.',
          },
          'Peel & Stick': {
            subtitle: 'Self-adhesive, repositionable',
            description:
              'Our Peel & Stick wallpaper is a self-adhesive material that is easy to install and remove without damaging walls. It is repositionable, making it great for renters or anyone who wants a temporary design change.',
          },
          'LUXE Peel & Stick': {
            subtitle: 'Premium non-woven, self-adhesive',
            description:
              'Our LUXE Peel & Stick is the same premium non-woven material as LUXE Traditional, with the same smooth, matte finish and rich color reproduction — but with a pre-applied adhesive backing. Simply peel and stick to install, and reposition or remove without damaging your walls. The best of both worlds — easy install with a luxurious finish.',
          },
        };
      }

      connectedCallback() {
        this.parseData();
        this.cacheDOM();
        this.bindEvents();
      }

      parseData() {
        const dataEl = document.getElementById('wallpaper-wizard-data');
        if (!dataEl) {
          console.error('WallpaperWizard: missing #wallpaper-wizard-data JSON block');
          return;
        }
        const data = JSON.parse(dataEl.textContent);
        this.variants = data.variants || [];
        this.productTitle = data.productTitle || '';
        this.availableHeights = data.availableHeights || [];
        this.materialTypes = data.materialTypes || [];
        this.sampleVariants = data.sampleVariants || [];
        this.globalDiscount = data.globalDiscount || 0;
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      cacheDOM() {
        // Unit toggle
        this.unitBtns = this.querySelectorAll('.wizard-unit-toggle__btn');

        // Dimension inputs
        this.widthInput = this.querySelector('[data-dimension="width"]');
        this.heightInput = this.querySelector('[data-dimension="height"]');
        this.widthSuffix = this.querySelector('[data-suffix="width"]');
        this.heightSuffix = this.querySelector('[data-suffix="height"]');

        // Printed area
        this.printedArea = this.querySelector('.wizard-printed-area');
        this.printedOriginal = this.querySelector('.wizard-printed-area__original');
        this.printedAdjusted = this.querySelector('.wizard-printed-area__adjusted');
        this.printedSqft = this.querySelector('.wizard-printed-area__sqft');

        // CTA
        this.ctaButton = this.querySelector('.wizard-cta-button');

        // Materials
        this.materialsContainer = this.querySelector('.wizard-materials');

        // Summary
        this.summarySection = this.querySelector('.wizard-summary');
        this.summaryRows = this.querySelector('.wizard-summary__rows');
        this.summaryTotalValue = this.querySelector('.wizard-summary__total-value');

        // Add to cart
        this.addToCartBtn = this.querySelector('.wizard-add-to-cart');
        this.addToCartText = this.querySelector('.wizard-add-to-cart__text');
        this.addToCartSpinner = this.querySelector('.wizard-add-to-cart__spinner');

        // Samples
        this.samplesTrigger = this.querySelector('.wizard-samples__add-trigger');
        this.samplesExpanded = this.querySelector('.wizard-samples-expanded');
        this.samplesMaterials = this.querySelector('.wizard-samples__materials');
        this.samplesAddBtn = this.querySelector('.wizard-samples__add-btn');
        this.samplesAddBtnText = this.querySelector('.wizard-samples__add-btn-text');
        this.samplesAddSpinner = this.querySelector('.wizard-samples__add-spinner');

        // Error
        this.errorEl = this.querySelector('.wizard-error');

        // Product title price block (updates as the wizard is configured)
        const productSection = this.closest('.shopify-section') || document;
        this.priceEl = productSection.querySelector('[id^="price-"] .price');
      }

      bindEvents() {
        // Unit toggle
        this.unitBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.handleUnitToggle(btn.dataset.unit));
        });

        // Dimension inputs
        this.widthInput.addEventListener('input', () => this.handleDimensionChange());
        this.heightInput.addEventListener('input', () => this.handleDimensionChange());

        // CTA
        this.ctaButton.addEventListener('click', () => this.handleCTAClick());

        // Add to cart
        this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());

        // Samples
        this.samplesTrigger.addEventListener('click', () => this.handleSampleToggle());
        this.samplesAddBtn.addEventListener('click', () => this.handleSampleAddToCart());
      }

      /* ========== Unit Toggle ========== */
      handleUnitToggle(unit) {
        if (unit === this.state.unit) return;

        const prevUnit = this.state.unit;
        this.state.unit = unit;

        // Update toggle buttons
        this.unitBtns.forEach((btn) => {
          btn.classList.toggle('wizard-unit-toggle__btn--active', btn.dataset.unit === unit);
        });

        // Update suffix labels
        const suffix = unit === 'inches' ? 'in' : 'cm';
        this.widthSuffix.textContent = suffix;
        this.heightSuffix.textContent = suffix;

        // Convert existing values
        if (this.widthInput.value) {
          const val = parseFloat(this.widthInput.value);
          if (!isNaN(val)) {
            this.widthInput.value =
              unit === 'cm'
                ? Math.round(val * 2.54 * 10) / 10
                : Math.round((val / 2.54) * 10) / 10;
          }
        }
        if (this.heightInput.value) {
          const val = parseFloat(this.heightInput.value);
          if (!isNaN(val)) {
            this.heightInput.value =
              unit === 'cm'
                ? Math.round(val * 2.54 * 10) / 10
                : Math.round((val / 2.54) * 10) / 10;
          }
        }

        this.handleDimensionChange();
      }

      /* ========== Dimension Inputs ========== */
      handleDimensionChange() {
        const rawW = parseFloat(this.widthInput.value);
        const rawH = parseFloat(this.heightInput.value);

        if (isNaN(rawW) || isNaN(rawH) || rawW <= 0 || rawH <= 0) {
          this.printedArea.style.display = 'none';
          this.ctaButton.style.display = 'none';
          // Reset downstream
          this.state.showMaterials = false;
          this.materialsContainer.style.display = 'none';
          this.summarySection.style.display = 'none';
          this.addToCartBtn.style.display = 'none';
          this.state.selectedMaterial = null;
          this.state.selectedVariantId = null;
          return;
        }

        this.state.rawWidth = rawW;
        this.state.rawHeight = rawH;

        // Convert to inches
        const widthInches = this.state.unit === 'cm' ? rawW / 2.54 : rawW;
        const heightInches = this.state.unit === 'cm' ? rawH / 2.54 : rawH;

        // Adjust width: round up to nearest multiple of 19
        this.state.adjustedWidthInches = Math.ceil(widthInches / 19) * 19;

        // Adjust height: convert to feet, round up to nearest even foot, clamp to available variant heights
        const heightFeet = heightInches / 12;
        let adjustedHeightFeet = Math.ceil(heightFeet / 2) * 2;

        // Clamp to available variant heights
        if (this.availableHeights.length > 0) {
          const minH = Math.min(...this.availableHeights);
          const maxH = Math.max(...this.availableHeights);
          if (adjustedHeightFeet < minH) adjustedHeightFeet = minH;
          if (adjustedHeightFeet > maxH) adjustedHeightFeet = maxH;

          // Find the nearest available height that is >= adjustedHeightFeet
          const validHeight = this.availableHeights
            .sort((a, b) => a - b)
            .find((h) => h >= adjustedHeightFeet);
          if (validHeight) {
            adjustedHeightFeet = validHeight;
          } else {
            adjustedHeightFeet = maxH;
          }
        }

        this.state.adjustedHeightFeet = adjustedHeightFeet;
        this.state.panelCount = Math.ceil(widthInches / 19);
        this.state.areaSqFt =
          (this.state.adjustedWidthInches / 12) * this.state.adjustedHeightFeet;

        // Show printed area
        this.renderPrintedArea(rawW, rawH);
        this.printedArea.style.display = '';
        this.ctaButton.style.display = '';

        // If materials were already showing, re-calculate prices
        if (this.state.showMaterials) {
          this.renderMaterials();
          // If a material was selected, re-select it to update summary
          if (this.state.selectedMaterial) {
            this.selectMaterial(this.state.selectedMaterial);
          }
        }
      }

      renderPrintedArea(rawW, rawH) {
        const unit = this.state.unit === 'inches' ? 'in' : 'cm';
        this.printedOriginal.textContent = `${rawW} × ${rawH} ${unit}`;

        // Show adjusted in both inches and feet
        const adjW = this.state.adjustedWidthInches;
        const adjH = this.state.adjustedHeightFeet;
        this.printedAdjusted.textContent = `${adjW}″ × ${adjH}ft (${this.state.panelCount} panel${this.state.panelCount !== 1 ? 's' : ''})`;

        this.printedSqft.textContent = `${this.state.areaSqFt.toFixed(1)} sq ft printed area`;
      }

      /* ========== CTA / Materials ========== */
      handleCTAClick() {
        this.state.showMaterials = true;
        this.renderMaterials();
        this.materialsContainer.style.display = '';
        // Scroll materials into view
        this.materialsContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      renderMaterials() {
        this.materialsContainer.innerHTML = '';

        this.materialTypes.forEach((materialName) => {
          // Find the variant matching this material + adjusted height
          const variant = this.findVariant(materialName, this.state.adjustedHeightFeet);
          if (!variant) return;

          const pricePerPanel = variant.price / 100; // Shopify prices in cents
          const originalPrice = pricePerPanel * this.state.panelCount;
          const discountPercent = this.globalDiscount;
          const totalPrice =
            discountPercent > 0 ? originalPrice * (1 - discountPercent / 100) : originalPrice;
          const savings = originalPrice - totalPrice;
          const pricePerSqFt = totalPrice / this.state.areaSqFt;

          const meta = this.materialMeta[materialName] || {
            subtitle: '',
            description: '',
          };

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'wizard-material-btn';
          btn.dataset.material = materialName;
          btn.dataset.variantId = variant.id;

          if (!variant.available) {
            btn.classList.add('wizard-material-btn--unavailable');
          }

          btn.innerHTML = `
            <div class="wizard-material-btn__info">
              <div class="wizard-material-btn__title">
                <span>${materialName}</span>
                <button type="button" class="wizard-material-btn__question" aria-label="More info about ${materialName}">?</button>
              </div>
              <div class="wizard-material-btn__subtitle">${meta.subtitle}</div>
              <div class="wizard-material-detail" style="display:none;">
                <p>${meta.description}</p>
              </div>
            </div>
            <div class="wizard-material-btn__pricing">
              ${
                discountPercent > 0
                  ? `<span class="wizard-material-btn__original-price">$${originalPrice.toFixed(2)}</span>`
                  : ''
              }
              <span class="wizard-material-btn__total-price">$${totalPrice.toFixed(2)}</span>
              <span class="wizard-material-btn__sqft-price">$${pricePerSqFt.toFixed(2)}/sq ft</span>
            </div>
            ${!variant.available ? '<div class="wizard-material-btn__unavailable-badge">Unavailable</div>' : ''}
          `;

          // Material button click
          btn.addEventListener('click', (e) => {
            if (!variant.available) return;
            // Don't trigger if clicking the question bubble
            if (e.target.closest('.wizard-material-btn__question')) return;
            this.selectMaterial(materialName);
          });

          // Question bubble
          const questionBtn = btn.querySelector('.wizard-material-btn__question');
          questionBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const detail = btn.querySelector('.wizard-material-detail');
            const isVisible = detail.style.display !== 'none';
            detail.style.display = isVisible ? 'none' : '';
          });

          this.materialsContainer.appendChild(btn);
        });
      }

      findVariant(material, heightFeet) {
        // Look for variant where option1 = material and option2 contains the height
        // Panel size option likely formatted as "X ft" or "Xft" or just "X"
        return this.variants.find((v) => {
          const matMatch = v.option1 === material;
          if (!matMatch) return false;
          // Parse height from option2
          const parsed = parseInt(v.option2, 10);
          return parsed === heightFeet;
        });
      }

      selectMaterial(materialName) {
        const btn = this.materialsContainer.querySelector(
          `[data-material="${materialName}"]`
        );
        if (!btn) return;

        const variantId = btn.dataset.variantId;
        this.state.selectedMaterial = materialName;
        this.state.selectedVariantId = variantId;

        // Highlight selected
        this.materialsContainer.querySelectorAll('.wizard-material-btn').forEach((b) => {
          b.classList.toggle(
            'wizard-material-btn--selected',
            b.dataset.material === materialName
          );
        });

        // Calculate pricing for summary
        const variant = this.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;

        const pricePerPanel = variant.price / 100;
        const originalPrice = pricePerPanel * this.state.panelCount;
        const discountPercent = this.globalDiscount;
        const totalPrice =
          discountPercent > 0 ? originalPrice * (1 - discountPercent / 100) : originalPrice;
        const savings = originalPrice - totalPrice;
        const pricePerSqFt = totalPrice / this.state.areaSqFt;

        // Render summary
        this.renderSummary({
          print: this.productTitle,
          dimensions: `${this.state.adjustedWidthInches}″ × ${this.state.adjustedHeightFeet}ft`,
          material: materialName,
          panels: this.state.panelCount,
          area: `${this.state.areaSqFt.toFixed(1)} sq ft`,
          rate: `$${pricePerSqFt.toFixed(2)}/sq ft`,
          savings: discountPercent > 0 ? `$${savings.toFixed(2)} (${discountPercent}% off)` : '—',
          total: totalPrice,
        });

        // Update the product title price block to reflect the configured total
        this.updateProductPrice(totalPrice, originalPrice, discountPercent);

        this.summarySection.style.display = '';
        this.addToCartBtn.style.display = '';

        // Scroll summary into view
        this.summarySection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      updateProductPrice(total, original, discountPercent) {
        if (!this.priceEl) return;

        const formatMoney = (value) => `$${value.toFixed(2)}`;
        const onSale = discountPercent > 0 && original > total;

        this.priceEl.classList.toggle('price--on-sale', onSale);

        const regular = this.priceEl.querySelector('.price__regular .price-item--regular');
        if (regular) regular.textContent = formatMoney(total);

        const saleItem = this.priceEl.querySelector('.price__sale .price-item--sale');
        if (saleItem) saleItem.textContent = formatMoney(total);

        const compareItem = this.priceEl.querySelector('.price__sale s.price-item--regular');
        if (compareItem) compareItem.textContent = formatMoney(original);
      }

      renderSummary(data) {
        const rows = [
          { label: 'Print', value: data.print },
          { label: 'Dimensions', value: data.dimensions },
          { label: 'Material', value: data.material },
          { label: 'Panels', value: data.panels },
          { label: 'Area', value: data.area },
          { label: 'Rate', value: data.rate },
          { label: 'Savings', value: data.savings },
        ];

        this.summaryRows.innerHTML = rows
          .map(
            (r) =>
              `<div class="wizard-summary__row">
                <span class="wizard-summary__row-label">${r.label}</span>
                <span class="wizard-summary__row-value">${r.value}</span>
              </div>`
          )
          .join('');

        this.summaryTotalValue.textContent = `$${data.total.toFixed(2)}`;
      }

      /* ========== Add To Cart ========== */
      async handleAddToCart() {
        if (!this.state.selectedVariantId || !this.state.panelCount) return;

        this.addToCartBtn.setAttribute('aria-disabled', 'true');
        this.addToCartBtn.classList.add('loading');
        this.addToCartSpinner.classList.remove('hidden');

        try {
          const response = await this.postToCart(
            this.state.selectedVariantId,
            this.state.panelCount
          );

          if (response.status) {
            this.showError(response.description || 'Could not add to cart.');
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'wallpaper-wizard',
              productVariantId: this.state.selectedVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wallpaper-wizard',
            productVariantId: this.state.selectedVariantId,
            cartData: response,
          });

          if (this.cart) {
            this.cart.renderContents(response);
          }
        } catch (e) {
          console.error(e);
          this.showError('Something went wrong. Please try again.');
        } finally {
          this.addToCartBtn.classList.remove('loading');
          this.addToCartBtn.removeAttribute('aria-disabled');
          this.addToCartSpinner.classList.add('hidden');
        }
      }

      /* ========== Samples ========== */
      handleSampleToggle() {
        const isOpen = this.samplesExpanded.style.display !== 'none';
        if (isOpen) {
          this.samplesExpanded.style.display = 'none';
          this.samplesTrigger.textContent = 'Add +';
          this.state.selectedSampleMaterial = null;
          return;
        }

        this.samplesExpanded.style.display = '';
        this.samplesTrigger.textContent = 'Close';
        this.renderSampleMaterials();
      }

      renderSampleMaterials() {
        this.samplesMaterials.innerHTML = '';

        this.sampleVariants.forEach((sv) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'wizard-sample-material-btn';
          btn.dataset.variantId = sv.id;
          btn.dataset.material = sv.material;

          const meta = this.materialMeta[sv.material] || { subtitle: '' };
          const price = (sv.price / 100).toFixed(2);

          btn.innerHTML = `
            <span class="wizard-sample-material-btn__name">${sv.material}</span>
            <span class="wizard-sample-material-btn__subtitle">${meta.subtitle}</span>
            <span class="wizard-sample-material-btn__price">$${price}</span>
          `;

          btn.addEventListener('click', () => {
            this.state.selectedSampleMaterial = sv.material;
            this.samplesMaterials
              .querySelectorAll('.wizard-sample-material-btn')
              .forEach((b) => {
                b.classList.toggle(
                  'wizard-sample-material-btn--selected',
                  b.dataset.material === sv.material
                );
              });
            this.samplesAddBtn.removeAttribute('disabled');
            this.samplesAddBtnText.textContent = `Add Sample to Cart - $${price}`;
          });

          this.samplesMaterials.appendChild(btn);
        });
      }

      async handleSampleAddToCart() {
        if (!this.state.selectedSampleMaterial) return;

        const sv = this.sampleVariants.find(
          (s) => s.material === this.state.selectedSampleMaterial
        );
        if (!sv) return;

        this.samplesAddBtn.setAttribute('aria-disabled', 'true');
        this.samplesAddBtn.classList.add('loading');
        this.samplesAddSpinner.classList.remove('hidden');

        try {
          const response = await this.postToCart(sv.id, 1);

          if (response.status) {
            this.showError(response.description || 'Could not add sample to cart.');
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wallpaper-wizard',
            productVariantId: sv.id,
            cartData: response,
          });

          if (this.cart) {
            this.cart.renderContents(response);
          }

          // Collapse samples section
          this.samplesExpanded.style.display = 'none';
          this.samplesTrigger.textContent = 'Add +';
          this.state.selectedSampleMaterial = null;
        } catch (e) {
          console.error(e);
          this.showError('Something went wrong. Please try again.');
        } finally {
          this.samplesAddBtn.classList.remove('loading');
          this.samplesAddBtn.removeAttribute('aria-disabled');
          this.samplesAddSpinner.classList.add('hidden');
        }
      }

      /* ========== Shared Helpers ========== */
      async postToCart(variantId, quantity) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);
        return response.json();
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.style.display = '';
        setTimeout(() => {
          this.errorEl.style.display = 'none';
        }, 5000);
      }
    }
  );
}
